import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Home,
  TrendingUp,
  TrendingDown,
  ScrollText,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "@/lib/api";
import { formatTRY } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";

function KpiCard({ icon: Icon, label, value, hint, tone = "default", testId, onClick }) {
  const toneMap = {
    default: "border-border",
    success: "border-l-4 border-l-[hsl(var(--success))]",
    warning: "border-l-4 border-l-[hsl(var(--warning))]",
    danger: "border-l-4 border-l-[hsl(var(--danger))]",
    brand: "border-l-4 border-l-brand",
  };
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      className={`rounded-md p-5 bg-card shadow-none hover:shadow-sm transition-all ${toneMap[tone]} ${
        onClick ? "cursor-pointer" : ""
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-md bg-secondary flex items-center justify-center">
          <Icon className="w-4 h-4 text-brand" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
          {label}
        </div>
      </div>
      <div className="font-heading text-2xl sm:text-3xl font-bold tracking-tight tabular-nums">
        {value}
      </div>
      {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [cashflow, setCashflow] = useState([]);
  const [pnl, setPnl] = useState([]);
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const promises = [
          api.get("/dashboard/summary"),
          api.get("/projects"),
        ];
        if (hasPerm("muhasebe.view")) promises.push(api.get("/dashboard/monthly-cashflow?months=12"));
        else promises.push(Promise.resolve({ data: [] }));
        if (hasPerm("projects.view")) promises.push(api.get("/dashboard/project-pnl"));
        else promises.push(Promise.resolve({ data: [] }));
        const [{ data: s }, { data: p }, { data: cf }, { data: pn }] = await Promise.all(promises);
        setSummary(s);
        setProjects(p);
        setCashflow(cf);
        setPnl(pn);
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!summary) return <div className="text-muted-foreground text-sm">Yükleniyor…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">
          Genel Bakış
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          Merhaba, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bugünün finansal ve operasyonel özeti.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KpiCard
          testId="kpi-projects"
          icon={Building2}
          label="Aktif Projeler"
          value={summary.total_projects}
          tone="brand"
          onClick={() => navigate("/projects")}
        />
        <KpiCard
          testId="kpi-units"
          icon={Home}
          label="Toplam Daire"
          value={summary.total_units}
        />
        <KpiCard
          testId="kpi-gelir"
          icon={TrendingUp}
          label="Toplam Gelir"
          value={formatTRY(summary.toplam_gelir)}
          tone="success"
        />
        <KpiCard
          testId="kpi-gider"
          icon={TrendingDown}
          label="Toplam Gider"
          value={formatTRY(summary.toplam_gider)}
          tone="danger"
        />
        <KpiCard
          testId="kpi-net"
          icon={TrendingUp}
          label="Net Bakiye"
          value={formatTRY(summary.net)}
          tone={summary.net >= 0 ? "success" : "danger"}
        />
        <KpiCard
          testId="kpi-checks"
          icon={ScrollText}
          label="Bekleyen Çekler"
          value={summary.bekleyen_cek}
          tone="warning"
          onClick={() => navigate("/cekler")}
        />
        <KpiCard
          testId="kpi-overdue"
          icon={AlertTriangle}
          label="Gecikmiş Çekler"
          value={summary.gecikmis_cek}
          tone="danger"
          onClick={() => navigate("/cekler")}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight">
            Projeler
          </h2>
          <button
            data-testid="dashboard-view-all-projects"
            onClick={() => navigate("/projects")}
            className="text-xs uppercase tracking-widest font-semibold text-brand hover:underline flex items-center gap-1"
          >
            Tümünü Gör <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.slice(0, 6).map((p) => (
            <Card
              key={p.id}
              data-testid={`dashboard-project-${p.id}`}
              onClick={() => navigate(`/project/${p.id}`)}
              className="p-5 cursor-pointer hover:shadow-sm transition-shadow shadow-none rounded-md"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-brand" />
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${
                    p.status === "insaat"
                      ? "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"
                      : p.status === "tamamlandi"
                      ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"
                      : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {p.status === "insaat" ? "İnşaat" : p.status === "tamamlandi" ? "Tamamlandı" : "Planlama"}
                </span>
              </div>
              <div className="font-heading font-semibold text-lg tracking-tight mb-1">
                {p.name}
              </div>
              <div className="text-xs text-muted-foreground mb-3">{p.address}</div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Bütçe</span>
                <span className="tabular-nums font-medium">{formatTRY(p.budget)}</span>
              </div>
            </Card>
          ))}
          {projects.length === 0 && (
            <div className="col-span-full text-sm text-muted-foreground p-8 text-center border border-dashed border-border rounded-md">
              Henüz proje eklenmedi.
            </div>
          )}
        </div>
      </div>

      {/* Monthly cashflow chart */}
      {cashflow.length > 0 && (
        <div data-testid="dashboard-cashflow">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Aylık Gelir / Gider (Son 12 Ay)</h2>
          <Card className="p-4 shadow-none rounded-md">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={cashflow} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                />
                <Tooltip
                  formatter={(v) => formatTRY(v)}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="gelir" name="Gelir" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="gider" name="Gider" fill="hsl(var(--danger))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* Project P&L */}
      {pnl.length > 0 && (
        <div data-testid="dashboard-project-pnl">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Proje Bazlı Kâr / Zarar</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pnl.map((p) => {
              const pct = Math.min(100, Math.round(p.budget_used_pct || 0));
              const overBudget = p.total_out > p.budget && p.budget > 0;
              return (
                <Card
                  key={p.id}
                  data-testid={`pnl-card-${p.id}`}
                  className={`p-5 shadow-none rounded-md border-l-4 cursor-pointer hover:shadow-sm transition-shadow border-l-brand`}
                  onClick={() => navigate(`/project/${p.id}`)}
                >
                  <div className="font-heading font-semibold text-base tracking-tight mb-3">{p.name}</div>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Bütçe</span>
                      <span className="tabular-nums font-medium">{formatTRY(p.budget)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Gelir</span>
                      <span className="tabular-nums font-medium text-[hsl(var(--success))]">{formatTRY(p.gelir)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Gider</span>
                      <span className="tabular-nums font-medium text-[hsl(var(--danger))]">{formatTRY(p.gider)}</span>
                    </div>
                    {p.budget > 0 && (
                      <div className="pt-2 border-t border-border">
                        <div className="flex items-center justify-between text-[10px] mb-1">
                          <span className="text-muted-foreground">Bütçe Kullanımı</span>
                          <span className={`font-semibold ${overBudget ? "text-[hsl(var(--danger))]" : ""}`}>%{pct}</span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${overBudget ? "bg-[hsl(var(--danger))]" : pct > 80 ? "bg-[hsl(var(--warning))]" : "bg-brand"}`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
